import { useState } from 'react';

export function getSessionStorageValue<T>(key: string): T | null {
  try {
    const item = window.sessionStorage.getItem(key.toUpperCase());
    return item ? JSON.parse(item) : null;
  } catch (error) {
    return null;
  }
}

export function setSessionStorageValue<T>(key: string, valueToStore: T) {
  try {
    if (valueToStore === null || valueToStore === undefined) {
      window.sessionStorage.removeItem(key.toUpperCase());
    } else {
      window.sessionStorage.setItem(key.toUpperCase(), JSON.stringify(valueToStore));
    }
  } catch (error) {
    console.log(error);
  }
}

function useSessionStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    return getSessionStorageValue(key) || initialValue;
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      setSessionStorageValue(key, valueToStore);
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };
  return { storedValue, setValue } as const;
}

export default useSessionStorage;
